import type Konva from 'konva';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { Rect } from 'react-konva';
import { getCellRects } from '~/features/grid/cellRects';
import { useStore } from '~/store';
import type { Rect as RectType } from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';
import { boxRefEquals } from '~/types/selection';

type Draft = { x: number; y: number; w: number; h: number };

const MARQUEE_FILL = 'rgba(255, 179, 0, 0.15)';
const MARQUEE_STROKE = '#FFB300';

/** True when two source-image rects overlap by at least one pixel. */
function rectsOverlap(a: RectType, b: RectType): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Pure helper: given a list of cells (each with a rect in source-image coords)
 * and a marquee rect, return the BoxRefs of cells that overlap the marquee by
 * at least one pixel.
 */
export function cellsOverlappingMarquee(
  cells: Array<{ box: BoxRef; rect: RectType }>,
  marquee: RectType,
): BoxRef[] {
  if (marquee.w <= 0 || marquee.h <= 0) return [];
  return cells.filter(({ rect }) => rectsOverlap(rect, marquee)).map(({ box }) => box);
}

/** Indices of blobs whose effective bbox overlaps the marquee by ≥ 1 px. */
export function blobsOverlappingMarquee(
  bboxes: Array<RectType | null>,
  marquee: RectType,
): number[] {
  if (marquee.w <= 0 || marquee.h <= 0) return [];
  const out: number[] = [];
  bboxes.forEach((rect, i) => {
    if (rect && rectsOverlap(rect, marquee)) out.push(i);
  });
  return out;
}

export function RegionSelectOverlay({ stageRef }: { stageRef: RefObject<Konva.Stage | null> }) {
  const drawMode = useStore((s) => s.editor.drawMode);
  const setDrawMode = useStore((s) => s.setDrawMode);
  const setSelectedBoxes = useStore((s) => s.setSelectedBoxes);
  const setSelectedBlobs = useStore((s) => s.setSelectedBlobs);
  const clearBoxSelection = useStore((s) => s.clearBoxSelection);
  const clearBlobSelection = useStore((s) => s.clearBlobSelection);

  const [draft, setDraft] = useState<Draft | null>(null);
  const shiftHeldRef = useRef(false);

  useEffect(() => {
    if (drawMode !== 'selectRegion') {
      setDraft(null);
      return;
    }

    const stage = stageRef.current;
    if (!stage) return;

    let start: { x: number; y: number } | null = null;

    const onDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      shiftHeldRef.current = 'shiftKey' in e.evt && e.evt.shiftKey;
      start = pos;
      setDraft({ x: pos.x, y: pos.y, w: 0, h: 0 });
    };

    const onMove = () => {
      if (!start) return;
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      setDraft({
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
      });
    };

    const onUp = () => {
      if (!start) return;
      const pos = stage.getRelativePointerPosition() ?? start;
      const marquee: RectType = {
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
      };
      const shiftHeld = shiftHeldRef.current;
      start = null;
      shiftHeldRef.current = false;
      setDraft(null);
      setDrawMode('none');

      // Treat tiny drags as click-cancel — reset without selecting.
      if (marquee.w < 1 || marquee.h < 1) return;

      const state = useStore.getState();
      if (state.pin.framingMode === 'pin') {
        const blobs = state.pin.blobs;
        if (!blobs) return;
        const bboxes = blobs.map((b, i) => state.pin.overrides[i]?.bbox ?? b.bbox);
        const matched = blobsOverlappingMarquee(bboxes, marquee);
        if (shiftHeld) {
          const merged = [...state.editor.selectedBlobs];
          for (const i of matched) if (!merged.includes(i)) merged.push(i);
          setSelectedBlobs(merged);
        } else if (matched.length === 0) {
          clearBlobSelection();
        } else {
          setSelectedBlobs(matched);
        }
        return;
      }

      const cells = getCellRects(state.grid);
      const matched = cellsOverlappingMarquee(cells, marquee);
      if (shiftHeld) {
        const merged = [...state.editor.selectedBoxes];
        for (const box of matched) {
          if (!merged.some((x) => boxRefEquals(x, box))) merged.push(box);
        }
        setSelectedBoxes(merged);
      } else if (matched.length === 0) {
        clearBoxSelection();
      } else {
        setSelectedBoxes(matched);
      }
    };

    stage.on('mousedown.regionSelect touchstart.regionSelect', onDown);
    stage.on('mousemove.regionSelect touchmove.regionSelect', onMove);
    stage.on('mouseup.regionSelect touchend.regionSelect', onUp);

    return () => {
      stage.off('mousedown.regionSelect touchstart.regionSelect');
      stage.off('mousemove.regionSelect touchmove.regionSelect');
      stage.off('mouseup.regionSelect touchend.regionSelect');
    };
  }, [
    drawMode,
    stageRef,
    setDrawMode,
    setSelectedBoxes,
    setSelectedBlobs,
    clearBoxSelection,
    clearBlobSelection,
  ]);

  if (drawMode !== 'selectRegion' || !draft) return null;

  return (
    <Rect
      x={draft.x}
      y={draft.y}
      width={draft.w}
      height={draft.h}
      fill={MARQUEE_FILL}
      stroke={MARQUEE_STROKE}
      strokeWidth={2}
      strokeScaleEnabled={false}
      listening={false}
    />
  );
}
